import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { graphqlApi } from '../../services/graphql';
import { Spinner } from '../common/Spinner';
import ChannelIcon from '../campaigns/ChannelIcon';

/**
 * 캠페인 매칭 현황 — admin view of every match (application) across all
 * campaigns. Lets an admin browse each campaign and expand it to see its
 * applicants (influencer, status, applied date, channels).
 *
 * Data is assembled entirely from existing public/authenticated queries —
 * no admin-only resolver required:
 *   - getCampaigns({})          → all campaigns (campaignId → campaign)
 *   - getMatches({})            → all matches (full paginated scan)
 *   - getInfluencersByIds(ids)  → influencer name/nickname/profile enrichment
 */

interface SocialAccount {
  key: string;
  account: string;
}

interface MatchRow {
  matchId: string;
  campaignId: string;
  influencerId: string;
  userId: string;
  status: string;
  appliedAt?: string;
  createdAt?: string;
  selectedMissions?: string[];
  influencerName?: string;
  profileImage?: string;
  socials?: SocialAccount[];
}

interface CampaignGroup {
  campaignId: string;
  campaignName: string;
  brandName: string;
  matches: MatchRow[];
  counts: Record<string, number>;
}

/**
 * Turn a stored SNS account value into an absolute, clickable URL.
 * Accounts are usually full URLs but can be bare handles (e.g. "@me").
 * Returns null when we can't build a sane link.
 */
const buildSocialHref = (channelKey: string, account?: string | null): string | null => {
  if (!account) return null;
  const raw = account.trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('www.')) return `https://${raw}`;
  const handle = raw.replace(/^@/, '');
  switch (channelKey) {
    case 'instagram':
      return `https://instagram.com/${handle}`;
    case 'youtube':
      return `https://youtube.com/@${handle}`;
    case 'tiktok':
      return `https://tiktok.com/@${handle}`;
    case 'twitter':
      return `https://twitter.com/${handle}`;
    case 'blog':
    case 'naverblog':
      if (/\./.test(raw)) return `https://${raw}`;
      return `https://blog.naver.com/${handle}`;
    default:
      return /\./.test(raw) ? `https://${raw}` : null;
  }
};

/** Extract non-empty social accounts from an influencer's socialMediaHandles. */
const extractSocials = (handles: any): SocialAccount[] => {
  if (!handles || typeof handles !== 'object') return [];
  const order = ['instagram', 'youtube', 'naverblog', 'tiktok', 'twitter', 'blog'];
  const out: SocialAccount[] = [];
  for (const key of order) {
    const account = handles[key]?.account;
    if (account && String(account).trim()) {
      out.push({ key, account: String(account).trim() });
    }
  }
  return out;
};

/** Status → { label i18n key, badge classes }. */
const STATUS_META: Record<string, { labelKey: string; defaultLabel: string; className: string }> = {
  applied: { labelKey: 'matches.status.applied', defaultLabel: '지원', className: 'bg-gray-100 text-gray-700' },
  shortlisted: { labelKey: 'matches.status.shortlisted', defaultLabel: '1차선정', className: 'bg-amber-100 text-amber-700' },
  requested: { labelKey: 'matches.status.requested', defaultLabel: '진행요청', className: 'bg-blue-100 text-blue-700' },
  accepted: { labelKey: 'matches.status.accepted', defaultLabel: '진행확정', className: 'bg-green-100 text-green-700' },
  completed: { labelKey: 'matches.status.completed', defaultLabel: '완료', className: 'bg-indigo-100 text-indigo-700' },
  rejected: { labelKey: 'matches.status.rejected', defaultLabel: '거절', className: 'bg-red-100 text-red-700' },
  not_selected: { labelKey: 'matches.status.not_selected', defaultLabel: '미선정', className: 'bg-gray-100 text-gray-500' },
  canceled: { labelKey: 'matches.status.canceled', defaultLabel: '취소', className: 'bg-gray-100 text-gray-500' },
  expired: { labelKey: 'matches.status.expired', defaultLabel: '만료', className: 'bg-gray-100 text-gray-500' },
};

const CampaignMatchesPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();

  const [groups, setGroups] = useState<CampaignGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Page through EVERY campaign — a single getCampaigns call is capped at
        // 100 rows, so campaigns beyond that window would otherwise resolve as
        // "(삭제된 캠페인)". Follow the nextToken cursor until it's exhausted.
        const fetchAllCampaigns = async (): Promise<any[]> => {
          const all: any[] = [];
          let token: string | undefined;
          // Guard against an unexpected non-terminating cursor.
          for (let i = 0; i < 100; i++) {
            const res = await graphqlApi.getCampaigns({}, 100, token);
            if (!res.success || !res.data) break;
            all.push(...(res.data.campaigns || []));
            token = res.data.nextToken || undefined;
            if (!token) break;
          }
          return all;
        };

        const [matchRes, campaigns] = await Promise.all([
          graphqlApi.getMatches({}),
          fetchAllCampaigns(),
        ]);

        const rawMatches: any[] =
          matchRes.success && matchRes.data ? matchRes.data.matches || [] : [];

        // Enrich influencer display names in one bulk lookup.
        const uniqueInfluencerIds = Array.from(
          new Set(rawMatches.map((m) => m.influencerId).filter(Boolean)),
        );
        const influencerMap: Record<string, any> = {};
        if (uniqueInfluencerIds.length > 0) {
          try {
            const infRes = await graphqlApi.getInfluencersByIds(uniqueInfluencerIds);
            if (infRes.success && infRes.data) {
              for (const inf of infRes.data) influencerMap[inf.influencerId] = inf;
            }
          } catch {
            /* enrichment is best-effort — fall back to id */
          }
        }

        const campaignMap: Record<string, any> = {};
        for (const c of campaigns) campaignMap[c.campaignId] = c;

        // Group matches by campaign.
        const byCampaign: Record<string, CampaignGroup> = {};
        for (const m of rawMatches) {
          const cid = m.campaignId;
          if (!cid) continue;
          if (!byCampaign[cid]) {
            const camp = campaignMap[cid];
            byCampaign[cid] = {
              campaignId: cid,
              campaignName: camp?.campaignName || t('matches.unknownCampaign', '(삭제된 캠페인)'),
              brandName: camp?.brandName || '',
              matches: [],
              counts: {},
            };
          }
          const inf = influencerMap[m.influencerId];
          byCampaign[cid].matches.push({
            matchId: m.matchId,
            campaignId: cid,
            influencerId: m.influencerId,
            userId: m.userId,
            status: m.status,
            appliedAt: m.appliedAt,
            createdAt: m.createdAt,
            selectedMissions: m.selectedMissions,
            influencerName: inf?.name || inf?.nickname,
            profileImage: inf?.profileImage,
            socials: extractSocials(inf?.socialMediaHandles),
          });
          byCampaign[cid].counts[m.status] = (byCampaign[cid].counts[m.status] || 0) + 1;
        }

        // Sort matches within each group (newest first), groups by applicant count desc.
        const result = Object.values(byCampaign).map((g) => {
          g.matches.sort((a, b) =>
            (b.appliedAt || b.createdAt || '').localeCompare(a.appliedAt || a.createdAt || ''),
          );
          return g;
        });
        result.sort((a, b) => b.matches.length - a.matches.length);

        if (!cancelled) setGroups(result);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load matches');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const totals = useMemo(() => {
    let campaignsWithApplicants = 0;
    let totalMatches = 0;
    let confirmed = 0;
    let today = 0;
    let thisMonth = 0;

    // Day/month boundaries in the admin's local timezone.
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

    for (const g of groups) {
      if (g.matches.length > 0) campaignsWithApplicants += 1;
      totalMatches += g.matches.length;
      confirmed += (g.counts['accepted'] || 0) + (g.counts['completed'] || 0);
      for (const m of g.matches) {
        const ts = new Date(m.appliedAt || m.createdAt || '').getTime();
        if (!Number.isFinite(ts)) continue;
        if (ts >= startOfDay) today += 1;
        if (ts >= startOfMonth) thisMonth += 1;
      }
    }
    return { campaignsWithApplicants, totalMatches, confirmed, today, thisMonth };
  }, [groups]);

  const filteredGroups = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(
      (g) =>
        g.campaignName.toLowerCase().includes(q) ||
        g.brandName.toLowerCase().includes(q) ||
        g.matches.some((m) => (m.influencerName || '').toLowerCase().includes(q)),
    );
  }, [groups, searchTerm]);

  const toggle = (campaignId: string) =>
    setExpanded((prev) => ({ ...prev, [campaignId]: !prev[campaignId] }));

  const fmtDate = (iso?: string) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return Number.isFinite(d.getTime())
      ? d.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })
      : '-';
  };

  const statusBadge = (status: string) => {
    const meta = STATUS_META[status] || {
      labelKey: '',
      defaultLabel: status,
      className: 'bg-gray-100 text-gray-600',
    };
    return (
      <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${meta.className}`}>
        {meta.labelKey ? t(meta.labelKey, meta.defaultLabel) : status}
      </span>
    );
  };

  return (
    <div className="min-h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="max-w-[1200px] mx-auto px-4 py-3">
        <nav className="text-sm text-gray-500">
          <button type="button" onClick={() => navigate('/admin')} className="hover:text-blue-600 transition-colors">
            {t('common:profileDropdown.adminDashboard')}
          </button>
          <span className="mx-1.5">&gt;</span>
          <span>{t('sidebar.activityManagement')}</span>
          <span className="mx-1.5">&gt;</span>
          <span className="text-gray-900 font-medium">{t('matches.title', '캠페인 매칭 현황')}</span>
        </nav>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 pb-16 space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('matches.title', '캠페인 매칭 현황')}</h1>

        {/* Stats strip */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left font-medium">{t('matches.stats.today', '오늘 지원')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('matches.stats.thisMonth', '이번달 지원')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('matches.stats.campaignsWithApplicants', '지원자 있는 캠페인')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('matches.stats.totalMatches', '총 지원')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('matches.stats.confirmed', '진행확정')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gray-100">
                <td className="px-4 py-3 text-lg font-bold text-blue-600">{loading ? '—' : totals.today.toLocaleString()}</td>
                <td className="px-4 py-3 text-lg font-bold text-blue-600">{loading ? '—' : totals.thisMonth.toLocaleString()}</td>
                <td className="px-4 py-3 text-lg font-bold text-gray-900">{loading ? '—' : totals.campaignsWithApplicants.toLocaleString()}</td>
                <td className="px-4 py-3 text-lg font-bold text-gray-900">{loading ? '—' : totals.totalMatches.toLocaleString()}</td>
                <td className="px-4 py-3 text-lg font-bold text-gray-900">{loading ? '—' : totals.confirmed.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('matches.searchPlaceholder', '캠페인명, 브랜드명, 인플루언서명 검색')}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        {/* Campaign groups */}
        {loading ? (
          <div className="py-16 flex justify-center"><Spinner size="medium" /></div>
        ) : error ? (
          <div className="py-12 text-center text-sm text-red-600">{error}</div>
        ) : filteredGroups.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('matches.empty', '매칭 데이터가 없습니다.')}</div>
        ) : (
          <div className="space-y-3">
            {filteredGroups.map((g) => {
              const open = !!expanded[g.campaignId];
              return (
                <div key={g.campaignId} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  {/* Group header */}
                  <button
                    type="button"
                    onClick={() => toggle(g.campaignId)}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50 transition-colors"
                  >
                    {open ? (
                      <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{g.campaignName}</p>
                      {g.brandName && <p className="text-xs text-gray-400 truncate">{g.brandName}</p>}
                    </div>
                    {/* Status count chips */}
                    <div className="hidden sm:flex items-center gap-1.5 flex-wrap justify-end">
                      {Object.entries(g.counts).map(([status, n]) => (
                        <span key={status} className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                          {statusBadge(status)}
                          <span className="font-mono">{n}</span>
                        </span>
                      ))}
                    </div>
                    <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-blue-50 text-blue-700 flex-shrink-0">
                      {t('matches.applicantCount', { count: g.matches.length, defaultValue: '{{count}}명' })}
                    </span>
                  </button>

                  {/* Applicant rows */}
                  {open && (
                    <div className="border-t border-gray-100 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium">{t('matches.col.influencer', '인플루언서')}</th>
                            <th className="px-4 py-2 text-left font-medium">{t('matches.col.status', '상태')}</th>
                            <th className="px-4 py-2 text-left font-medium">{t('matches.col.sns', 'SNS')}</th>
                            <th className="px-4 py-2 text-left font-medium">{t('matches.col.channels', '채널')}</th>
                            <th className="px-4 py-2 text-left font-medium">{t('matches.col.appliedAt', '지원일')}</th>
                            <th className="px-4 py-2 text-right font-medium">{t('matches.col.detail', '상세')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.matches.map((m) => (
                            <tr key={m.matchId} className="border-t border-gray-100 hover:bg-gray-50">
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-2">
                                  {m.profileImage ? (
                                    <img src={m.profileImage} alt="" className="w-7 h-7 rounded-full object-cover" />
                                  ) : (
                                    <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-[11px] text-gray-500">
                                      {(m.influencerName || '?')[0]}
                                    </div>
                                  )}
                                  <span className="text-gray-900">{m.influencerName || m.influencerId}</span>
                                </div>
                              </td>
                              <td className="px-4 py-2.5">{statusBadge(m.status)}</td>
                              <td className="px-4 py-2.5">
                                {(m.socials || []).length > 0 ? (
                                  <div className="flex flex-col gap-1">
                                    {(m.socials || []).map((s) => {
                                      const href = buildSocialHref(s.key, s.account);
                                      return (
                                        <div key={s.key} className="flex items-center gap-1.5">
                                          <ChannelIcon channel={s.key === 'naverblog' ? 'blog' : s.key} className="w-3.5 h-3.5 flex-shrink-0" />
                                          {href ? (
                                            <a
                                              href={href}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                                            >
                                              {s.account}
                                            </a>
                                          ) : (
                                            <span className="text-gray-700 break-all">{s.account}</span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-2.5 text-gray-600">
                                {(m.selectedMissions || []).length > 0 ? (m.selectedMissions || []).join(', ') : '-'}
                              </td>
                              <td className="px-4 py-2.5 text-gray-500">{fmtDate(m.appliedAt || m.createdAt)}</td>
                              <td className="px-4 py-2.5 text-right">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/campaigns/${g.campaignId}/applicants`)}
                                  className="text-xs font-medium text-blue-600 hover:underline"
                                >
                                  {t('matches.viewApplicants', '지원자 관리')}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignMatchesPage;
